import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { StarIcon } from '@heroicons/react/20/solid';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import Api from '../../../services';
import { RootState } from '../../../redux/store';

interface AddReviewModalProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  productId: string;
  onSuccess: () => void;
  onUnauthorized: () => void;
  onAlreadyReviewed: () => void;
  onNoPurchase: () => void;
}

type FormData = {
  rating: number;
  description?: string | null;
  is_anonymous: boolean;
};

const schema = yup.object().shape({
  rating: yup
    .number()
    .min(1, 'Rating is required')
    .required('Rating is required'),
  description: yup.string().nullable().notRequired(),
  is_anonymous: yup.boolean().required(),
});

export default function AddReviewModal({
  isOpen,
  setIsOpen,
  productId,
  onSuccess,
  onUnauthorized,
  onAlreadyReviewed,
  onNoPurchase,
}: AddReviewModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const authenticated = useSelector((state: RootState) => state.auth.authenticated);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      rating: 4,
      description: null,
      is_anonymous: false,
    },
  });

  const rating = watch('rating');

  const onCancel = () => {
    setIsOpen(false);
    reset();
  };

  const onSubmit = async (data: FormData) => {
    if (!authenticated) {
      setIsOpen(false);
      onUnauthorized();
      return;
    }

    setLoading(true);
    try {
      await Api.retailProducts.createRetailProductReview(productId, data);
      setLoading(false);
      setIsOpen(false);
      onSuccess();
      reset();
    } catch (error: any) {
      setLoading(false);
      setIsOpen(false);

      if (error?.response?.status === 422) {
        const errorCode = error.response.data?.error;
        switch (errorCode) {
        case 'already_reviewed':
          onAlreadyReviewed();
          break;
        case 'no_successful_purchase':
          onNoPurchase();
          break;
        default:
          break;
        }
      } else {
        // console.error(t('shared.genericError'), error);
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsOpen(false)}
          className='bg-slate-900/20 backdrop-blur p-8 fixed inset-0 z-50 grid place-items-center overflow-y-scroll cursor-pointer'
        >
          <motion.div
            initial={{ scale: 0, rotate: '12.5deg' }}
            animate={{ scale: 1, rotate: '0deg' }}
            exit={{ scale: 0, rotate: '0deg' }}
            onClick={(e) => e.stopPropagation()}
            className='bg-white p-6 rounded-lg w-full max-w-lg shadow-xl cursor-default relative overflow-hidden'
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <h3 className='text-2xl font-bold text-center mb-2 text-primary'>
                {t('productDetails.reviews.addReview')}
              </h3>
              <p className='text-center mb-3 text-black'>
                {t('productDetails.reviews.reviewPrompt')}
              </p>
              <Controller
                name='rating'
                control={control}
                render={({ field }) => (
                  <div className='flex justify-center space-x-2 rtl:space-x-reverse'>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIcon
                        key={star}
                        onClick={() => field.onChange(star)}
                        className={`h-6 w-6 cursor-pointer ${
                          star <= rating ? 'text-primary' : 'text-primary/30'
                        }`}
                      />
                    ))}
                  </div>
                )}
              />
              {errors.rating && (
                <p className='text-red-500 text-xs text-center mt-1'>
                  {errors.rating.message}
                </p>
              )}

              <div className='mt-4'>
                <Controller
                  name='is_anonymous'
                  control={control}
                  render={({ field }) => (
                    <div className='flex items-center justify-center'>
                      <input
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        name={field.name}
                        checked={field.value}
                        id='is_anonymous'
                        type='checkbox'
                        className='h-4 w-4 ml-2 rounded border-gray-300 text-primary focus:ring-primary'
                      />
                      <label
                        htmlFor='is_anonymous'
                        className='ml-2 block text-xs font-medium text-black'
                      >
                        {t('productDetails.reviews.submitAsAnonymous')}
                      </label>
                    </div>
                  )}
                />
              </div>

              <div className='mt-4'>
                <Controller
                  name='description'
                  control={control}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      id='description'
                      rows={4}
                      className={`block w-full rounded-md border-0 py-2.5 text-black shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 ${
                        errors.description ? 'ring-red-500' : ''
                      }`}
                      placeholder={t(
                        'productDetails.reviews.commentPlaceholder',
                      )}
                      value={field.value ?? undefined}
                    />
                  )}
                />
                {errors.description && (
                  <p className='text-red-500 text-xs mt-1'>
                    {errors.description.message}
                  </p>
                )}
              </div>

              <div className='flex gap-2 mt-6'>
                <button
                  type='button'
                  onClick={onCancel}
                  className='bg-transparent text-primary hover:bg-white/10 transition-colors font-semibold w-full py-2 rounded'
                >
                  {t('shared.cancel')}
                </button>
                <button
                  type='submit'
                  disabled={loading}
                  className='bg-primary hover:opacity-90 transition-opacity text-white font-semibold w-full py-2 rounded disabled:opacity-50'
                >
                  {loading
                    ? t('shared.loading')
                    : t('productDetails.reviews.submit')}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
